import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MealCard from './MealCard';
import MealCardMobile from './MealCardMobile';
import EmptyMealCard from './EmptyMealCard';
import EmptyMealCardMobile from './EmptyMealCardMobile';
import PantryAndGroceriesDrawer from './PantryAndGroceriesDrawer';
import { useUpdateMealPlanPreferences } from '../hooks/updateMealPlanPreferences';
import { useAddSingleMealToMealPlan } from '../hooks/addSingleMealToMealPlan';
import { useAuth } from '../context/AuthContext';

import { useCSRF } from '../utils/csrfUtils';
import ErrorBoundary from './ErrorBoundary';

const MealPlan = ({mealPlanId, onStartCooking, isInPast, onMealRefresh, externalRefreshTrigger}) => {
    const navigate = useNavigate();
    const [mealPlan, setMealPlan] = useState({ meals: [] });
    const [isCurrentMealPlan, setIsCurrentMealPlan] = useState(null);
    const { pgUser, sessionToken } = useAuth();
    const [showModal, setShowModal] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [recipes, setRecipes] = useState([]);
    const [selectedMealType, setSelectedMealType] = useState('');
    const [servings, setServings] = useState(1);
    const [selectedRecipe, setSelectedRecipe] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [refreshingMealTime, setRefreshingMealTime] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorRefreshingMealTime, setErrorRefreshingMealTime] = useState(false);
    const [errorRefreshingMealTimeMessage, setErrorRefreshingMealTimeMessage] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showBottomNav, setShowBottomNav] = useState(window.innerWidth < 992);
    const [isPantryDrawerOpen, setIsPantryDrawerOpen] = useState(false);

    const { fetchWithCSRF, isLoading: csrfLoading } = useCSRF();
    
    // Toast notification state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('Grocery List Updated');
    
    // Preferences state
    const [breakfastMeals, setBreakfastMeals] = useState(pgUser.preferred_number_of_breakfast_meals);
    const [lunchMeals, setLunchMeals] = useState(pgUser.preferred_number_of_lunch_meals);
    const [dinnerMeals, setDinnerMeals] = useState(pgUser.preferred_number_of_dinner_meals);
    
    const { updateMealPlanPreferences, isUpdating } = useUpdateMealPlanPreferences();
    const { addSingleMealToMealPlan, isAdding } = useAddSingleMealToMealPlan();

    // Function to show toast notification
    const showGroceryListToast = (message) => {
        setToastMessage(message);
        setShowToast(true);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            setShowToast(false);
        }, 5000);
    };

    const showErrorRefreshingMealTimeToast = (message) => {
        setErrorRefreshingMealTimeMessage(message);
        setErrorRefreshingMealTime(true);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            setErrorRefreshingMealTime(false);
        }, 5000);
    };

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setShowBottomNav(window.innerWidth < 992);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchMealPlan = async () => {
            if (mealPlanId != null) {
                try {
                    if (!sessionToken) {
                        throw new Error('No valid session found');
                    }
                    const response = await fetchWithCSRF('/api/v1/meal-plan/' + mealPlanId + '/app_user/' + pgUser.id, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${sessionToken}`
                        },
                    });
                    const data = await response.json();
                    
                    // The new endpoint returns a single object, not an array
                    setMealPlan(data);
                    // Set current preferences from meal plan data
                    if (data) {
                        setBreakfastMeals(data.preferred_number_of_breakfast_meals);
                        setLunchMeals(data.preferred_number_of_lunch_meals);
                        setDinnerMeals(data.preferred_number_of_dinner_meals);
                    }
                } catch (error) {
                    console.error('Error fetching meal plan:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchMealPlan();
    }, [mealPlanId, isCurrentMealPlan, pgUser?.id, refreshTrigger, externalRefreshTrigger]);

    useEffect(() => {
        const fetchRecipes = async () => {
            if (showModal) {
                try {
                    if (!sessionToken) {
                        throw new Error('No valid session found');
                    }
                    const response = await fetchWithCSRF('/api/v1/recipe', {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${sessionToken}`
                        },
                    });
                    const data = await response.json();
                    setRecipes(data);
                } catch (error) {
                    console.error('Error fetching recipes:', error);
                }
            }
        };
        
        fetchRecipes();
    }, [showModal]);

    
    const handlePreferencesModalClose = () => setShowPreferencesModal(false);
    const handlePreferencesModalShow = () => setShowPreferencesModal(true);

    const handlePreferencesSubmit = async () => {
        try {
            const data = await updateMealPlanPreferences(
                mealPlanId, 
                pgUser.id, 
                breakfastMeals, 
                pgUser.preferred_breakfast_serving_size, 
                lunchMeals, 
                pgUser.preferred_lunch_serving_size, 
                dinnerMeals, 
                pgUser.preferred_dinner_serving_size
            );
            
            handlePreferencesModalClose();
            // Refresh the meal plan to show updated preferences
            setRefreshTrigger(prev => prev + 1);
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification
            showGroceryListToast('Meal plan preferences and grocery list updated');
        } catch (error) {
            console.error('Failed to update preferences:', error);
            showErrorRefreshingMealTimeToast('Error updating preferences. Please refresh the page and try again.');
        }
    };

    const handleAddMealToMealTime = async (mealTime) => {
        const servingSize = pgUser["preferred_" + mealTime + "_serving_size"];
        if (servingSize == null || servingSize == undefined || servingSize < 0) {
            servingSize = 4;
        }
        try {
            await addSingleMealToMealPlan(mealPlanId, pgUser.id, mealTime, servingSize, null); // null = random recipe
            // Refresh the meal plan to show the new meal
            setRefreshTrigger(prev => prev + 1);
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification
            // showGroceryListToast(`${mealTime.charAt(0).toUpperCase() + mealTime.slice(1)} meal added and grocery list updated`);
            showGroceryListToast(`Grocery list updated`);
        } catch (error) {
            console.error('Failed to add meal:', error);
            showErrorRefreshingMealTimeToast('Error adding meal. Please refresh the page and try again.');
        }
    };


    const handleMealTimeRefresh = async (mealTime) => {
        setRefreshingMealTime(mealTime);
        
        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF(`/api/v1/meal-plan/refresh/${mealTime}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    meal_plan_id: mealPlanId,
                    app_user_id: pgUser.id
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setRefreshTrigger(prev => prev + 1);
            setRefreshingMealTime(null);
            // Show toast notification
            showGroceryListToast(`Grocery list updated`);
        } catch (error) {
            console.error('Error refreshing meal time:', error);
            setRefreshingMealTime(null);
            showErrorRefreshingMealTimeToast(`Error refreshing ${mealTime} meals. Please refresh the page and try again.`);
        }
    };

    const handleMealRefresh = () => {
        // Trigger a refresh of the meal plan data
        setRefreshTrigger(prev => prev + 1);
        // Also call the parent callback if provided
        if (onMealRefresh) {
            onMealRefresh();
        }
    };

    const renderMealTimeContent = (mealTime) => {
        const meals = mealPlan.meals
            .filter(meal => meal.meal_of_day === mealTime)
            .sort((a, b) => (a.position || 0) - (b.position || 0)); // Sort by position (ascending)
        
        if (refreshingMealTime === mealTime) {
            // Create placeholder divs that match the current layout
            return (
                <div className="row justify-content-center position-relative">
                    {/* Maintain the same layout with invisible placeholders */}
                    {meals.map(meal => (
                        <div key={meal.id} className="col-12 col-sm-6 col-md-3 col-lg-6 mb-4" style={{ visibility: 'hidden' }}>
                            <div className="meal-card" style={{ height: '437px' }}></div>
                        </div>
                    ))}
                    {/* Overlay the spinner on top */}
                    <div className="position-absolute top-50 start-50 translate-middle">
                        {/* <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                            <span className="visually-hidden">Loading...</span>
                        </Spinner> */}
                    </div>
                </div>
            );
        }

        // Check if we should show preference message (when preference is set to 0 meals)
        const showPreferenceMessage = meals.length === 0 && (
            (mealTime === 'breakfast' && mealPlan.preferred_number_of_breakfast_meals === 0) ||
            (mealTime === 'lunch' && mealPlan.preferred_number_of_lunch_meals === 0) ||
            (mealTime === 'dinner' && mealPlan.preferred_number_of_dinner_meals === 0)
        );

        if (showPreferenceMessage) {
            if (mealTime === 'breakfast') {
                return <div className="text-muted">This Meal Plan is set to {breakfastMeals} breakfast meals. Click the Modify Preferences button to adjust.</div>;
            } else if (mealTime === 'lunch') {
                return <div className="text-muted">This Meal Plan is set to {lunchMeals} lunch meals. Click the Modify Preferences button to adjust.</div>;
            } else if (mealTime === 'dinner') {
                return <div className="text-muted">This Meal Plan is set to {dinnerMeals} dinner meals. Click the Modify Preferences button to adjust.</div>;
            }
        }

        // Conditionally render MealCardMobile for small screens, MealCard for larger screens
        if (isMobile) {
            return (
                <div className="meal-cards-mobile-container">
                    {meals.map(meal => (
                        meal.recipe_id === null ? (
                            <EmptyMealCardMobile
                                key={meal.id}
                                meal={meal}
                                onMealRefresh={handleMealRefresh}
                                onGroceryListRefresh={showGroceryListToast}
                                isInPast={isInPast}
                            />
                        ) : (
                            <MealCardMobile
                                key={meal.id}
                                meal={meal} 
                                onStartCooking={onStartCooking}
                                isInPast={isInPast}
                                onMealRefresh={handleMealRefresh}
                                onGroceryListRefresh={showGroceryListToast}
                            />
                        )
                    ))}
                    {/* Add new meal slot card at the end */}
                    {!isInPast && (
                        <EmptyMealCardMobile
                            key="new-meal-slot"
                            meal={null}
                            mealTime={mealTime}
                            onAddNewMeal={handleAddMealToMealTime}
                            isNewSlot={true}
                            isInPast={isInPast}
                        />
                    )}
                    {/* Review Items to Buy button for mobile */}
                    {!isInPast && (
                        <div style={{ 
                            padding: '10px 0',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <button 
                                className="btn-primary-rounded btn-lg px-4 py-2"
                                onClick={() => setIsPantryDrawerOpen(true)}
                                style={{ 
                                    fontSize: '1.25rem',
                                    fontWeight: '500',
                                    borderRadius: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    width: '100%',
                                    maxWidth: '350px',
                                    justifyContent: 'center'
                                }}
                            >
                                <img src="/icons/shopping-cart-solid_white.svg" alt="" style={{ width: '20px', height: '20px' }} />
                                Review Items to Buy
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <>
                <div className="row justify-content-start">
                    {meals.map(meal => (
                        <div key={meal.id} className="col-12 col-sm-5 col-md-5 col-lg-4 col-xl-3 mb-4">
                            {meal.recipe_id === null ? (
                                <EmptyMealCard
                                    meal={meal}
                                    onMealRefresh={handleMealRefresh}
                                    onGroceryListRefresh={showGroceryListToast}
                                    isInPast={isInPast}
                                />
                            ) : (
                                <MealCard 
                                    meal={meal} 
                                    onStartCooking={onStartCooking}
                                    isInPast={isInPast}
                                    onMealRefresh={handleMealRefresh}
                                    onGroceryListRefresh={showGroceryListToast}
                                />
                            )}
                        </div>
                    ))}
                    {/* Add new meal slot card at the end */}
                    {!isInPast && (
                        <div key="new-meal-slot" className="col-12 col-sm-5 col-md-5 col-lg-4 col-xl-3 mb-4">
                            <EmptyMealCard
                                meal={null}
                                mealTime={mealTime}
                                onAddNewMeal={handleAddMealToMealTime}
                                isNewSlot={true}
                                isInPast={isInPast}
                            />
                        </div>
                    )}
                </div>
                {/* Review Items to Buy button when BottomNav is shown */}
                {!isInPast && showBottomNav && (
                    <div style={{ 
                        padding: '10px 0',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <button 
                            className="btn-primary-rounded btn-lg px-4 py-2"
                            onClick={() => setIsPantryDrawerOpen(true)}
                            style={{ 
                                fontSize: '1.25rem',
                                fontWeight: '500',
                                borderRadius: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                width: '100%',
                                maxWidth: '350px',
                                justifyContent: 'center'
                            }}
                        >
                            <img src="/icons/shopping-cart-solid_white.svg" alt="" style={{ width: '20px', height: '20px' }} />
                            Review Items to Buy
                        </button>
                    </div>
                )}
            </>
        );
    };
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <ErrorBoundary fallbackMessage="Unable to load meal plan content. Please try refreshing the page or contact support if the issue persists.">
            <div className="meal-plan-container">

                

                {!isInPast && (null
                // <button 
                //     className="mb-3 btn-primary"
                //     onClick={handlePreferencesModalShow}
                //     title="Adjust meal plan preferences"
                // >
                //     {/* <img src="/icons/three-dots.svg" alt="Adjust meal preferences" style={{ width: '16px', height: '16px' }} /> */}
                //     Modify Meal Plan Preferences
                // </button>
                )}

            
            {/* <ErrorBoundary fallbackMessage="Unable to load breakfast meals. Please try refreshing the page.">
                <div className="breakfast-meals mt-1">
                    <h2>Breakfast</h2>
                    <div className="justify-content-center gap-2 mb-3">
                        <button 
                            className="btn-primary btn-sm px-3 py-2"
                            onClick={() => handleAddMealToMealTime('breakfast')}
                            disabled={isInPast || isAdding}
                            style={{ 
                                fontSize: '0.9rem',
                                padding: '2px 8px',
                                lineHeight: '1.2',
                                cursor: (isInPast || isAdding) ? 'not-allowed' : 'pointer',
                                opacity: (isInPast || isAdding) ? '0.3' : '1',
                                borderRadius: '4px'
                            }}
                        >
                            
                            Add a Meal
                        </button>
                        <button 
                            className="refresh-mealtime-btn" 
                            onClick={() => handleMealTimeRefresh('breakfast')}
                            disabled={isInPast || mealPlan.preferred_number_of_breakfast_meals === 0}
                            style={{ 
                                cursor: isInPast ? 'not-allowed' : 'pointer',
                                opacity: isInPast ? '0.3' : '1'
                            }}
                        >
                            <img 
                                src="/icons/refresh.svg" 
                                alt="" 
                                style={{ 
                                    animation: refreshingMealTime === 'breakfast' ? 'spin 1s linear infinite' : 'none'
                                }}
                            />
                            
                        </button>
                    </div>
                    {renderMealTimeContent('breakfast')}
                </div>
            </ErrorBoundary> */}


            {/* <ErrorBoundary fallbackMessage="Unable to load lunch meals. Please try refreshing the page.">
                <div className="lunch-meals mt-1">
                    <h2>Lunch</h2>
                    <div className="justify-content-center gap-2 mb-3">
                    <button 
                        className="btn-transparent"
                        onClick={() => handleAddMealToMealTime('lunch')}
                        disabled={isInPast || isAdding}
                        style={{ 
                            cursor: (isInPast || isAdding) ? 'not-allowed' : 'pointer',
                            opacity: (isInPast || isAdding) ? '0.3' : '1'
                        }}
                    >
                        <img src="/icons/plus-circle-green.svg" alt="Add Breakfast Meal" />
                    </button>
                        <button 
                            className="refresh-mealtime-btn" 
                            onClick={() => handleMealTimeRefresh('lunch')}
                            disabled={isInPast || mealPlan.preferred_number_of_lunch_meals === 0}
                            style={{ 
                                cursor: isInPast ? 'not-allowed' : 'pointer',
                                opacity: isInPast ? '0.3' : '1'
                            }}
                        >
                            <img 
                                src="/icons/refresh.svg" 
                                alt="" 
                                style={{ 
                                    animation: refreshingMealTime === 'lunch' ? 'spin 1s linear infinite' : 'none'
                                }}
                            />
                        </button>
                    </div>
                    
                    {renderMealTimeContent('lunch')}
                </div>
            </ErrorBoundary> */}

            <ErrorBoundary fallbackMessage="Unable to load dinner meals. Please try refreshing the page.">
                <div className="dinner-meals mt-1">
                    {/* <h2>Meals</h2> */}
                    {renderMealTimeContent('dinner')}
                </div>
            </ErrorBoundary>

            {/* Persistent Bottom Bar for Large Screens (where BottomNav is hidden) */}
            {!isInPast && !showBottomNav && (
                <div 
                    className="meal-plan-bottom-bar"
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
                        padding: '16px 24px',
                        zIndex: 1000
                    }}
                >
                    <div style={{ 
                        maxWidth: '1200px', 
                        margin: '0 auto',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <button 
                            className="btn-primary-rounded btn-lg px-4 py-2"
                            onClick={() => setIsPantryDrawerOpen(true)}
                            style={{ 
                                fontSize: '1.25rem',
                                fontWeight: '500',
                                borderRadius: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            <img src="/icons/shopping-cart-solid_white.svg" alt="" style={{ width: '20px', height: '20px' }} />
                            Review Items to Buy
                        </button>
                    </div>
                </div>
            )}


            {/* Preferences Modal */}
            <Modal show={showPreferencesModal} onHide={handlePreferencesModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Meal Plan Preferences</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-4">Adjust how many meals you'd like for this meal plan</p>
                    
                    <div className="mb-4">
                        <label className="form-label fw-bold">Breakfast Meals</label>
                        <div className="d-flex align-items-center justify-content-center gap-3">
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setBreakfastMeals(Math.max(0, breakfastMeals - 1))}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                            >
                                <img src="/icons/minus-circle-red.svg" alt="Add Breakfast Meal" />
                            </Button>
                            <span
                                style={{ 
                                    minWidth: '3rem', 
                                    textAlign: 'center', 
                                    fontSize: '1.2rem',
                                    fontWeight: '600'
                                }}
                            >
                                {breakfastMeals}
                            </span>
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setBreakfastMeals(breakfastMeals + 1)}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                                className=""
                            >
                                <img src="/icons/plus-circle-green.svg" alt="Add Breakfast Meal" />
                            </Button>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label fw-bold">Lunch Meals</label>
                        <div className="d-flex align-items-center justify-content-center gap-3">
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setLunchMeals(Math.max(0, lunchMeals - 1))}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                            >
                                <img src="/icons/minus-circle-red.svg" alt="Add Breakfast Meal" />
                            </Button>
                            <span
                                style={{ 
                                    minWidth: '3rem', 
                                    textAlign: 'center', 
                                    fontSize: '1.2rem',
                                    fontWeight: '600'
                                }}
                            >
                                {lunchMeals}
                            </span>
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setLunchMeals(lunchMeals + 1)}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                                className=""
                            >
                                <img src="/icons/plus-circle-green.svg" alt="Add Breakfast Meal" />
                            </Button>
                        </div>
                    </div>

                    <div className="mb-4">
                        <label className="form-label fw-bold">Dinner Meals</label>
                        <div className="d-flex align-items-center justify-content-center gap-3">
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setDinnerMeals(Math.max(0, dinnerMeals - 1))}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                            >
                                <img src="/icons/minus-circle-red.svg" alt="Add Breakfast Meal" />
                            </Button>
                            <span
                                style={{ 
                                    minWidth: '3rem', 
                                    textAlign: 'center', 
                                    fontSize: '1.2rem',
                                    fontWeight: '600'
                                }}
                            >
                                {dinnerMeals}
                            </span>
                            <Button 
                                variant="" 
                                size="md" 
                                onClick={() => setDinnerMeals(dinnerMeals + 1)}
                                style={{ minWidth: '2rem', fontWeight: 'bold' }}
                            >
                                <img src="/icons/plus-circle-green.svg" alt="Add Breakfast Meal" />
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handlePreferencesModalClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handlePreferencesSubmit}
                        disabled={isUpdating}
                        style={{ backgroundColor: '#52cd92', borderColor: '#52cd92' }}
                    >
                        {isUpdating ? 'Updating...' : 'Save Preferences'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Toast Notification */}
            {showToast && (
                <div style={{
                    position: 'fixed',
                    bottom: '15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#313638',
                    color: 'white',
                    padding: '16px 32px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    fontSize: '1rem',
                    fontWeight: '500',
                    minWidth: '250px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                }}
                onClick={() => setShowToast(false)}
                >
                    <img src="/icons/exclamation-outline.svg" alt="" style={{ width: '25px', height: '25px', filter: 'brightness(0) invert(1)' }} />
                    {toastMessage}
                </div>
            )}

            {/* Error Refreshing Toast Notification */}
            {errorRefreshingMealTime && (
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#dc3545',
                    color: 'white',
                    padding: '16px 32px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    fontSize: '1rem',
                    fontWeight: '500',
                    minWidth: '250px',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}
                onClick={() => setErrorRefreshingMealTime(false)}
                >
                    {errorRefreshingMealTimeMessage}
                </div>
            )}
            
            {/* Pantry and Groceries Drawer */}
            <PantryAndGroceriesDrawer
                isOpen={isPantryDrawerOpen}
                onClose={() => setIsPantryDrawerOpen(false)}
                mealPlanId={mealPlanId}
            />
            
            </div>
        </ErrorBoundary>
    );

};
export default MealPlan;